import React, { useEffect, useState } from 'react'
import { FilterMatchMode } from 'primereact/api'
import { Nullable } from 'primereact/ts-helpers'
import { DataTable, DataTableExpandedRows } from 'primereact/datatable'
import dayjs from 'dayjs'
import { Column } from 'primereact/column'
import { TableColumn } from './TableColumn/TableColumn'
import { TableHeader } from './TableHeader/TableHeader'
import { ExpandedRow } from './ExpandedRow/ExpandedRow'
import { useGetSystemLogsQuery } from '../../graphql/graphql'
import { OktaLog, TABLE_COLUMNS } from '../../constants/constants'

import 'primereact/resources/themes/lara-light-blue/theme.css'
import 'primereact/resources/primereact.min.css'
import 'primeicons/primeicons.css'

interface TableProps {
  email: string
  startTime: string
  endTime: string
}

export function Table({ email, startTime, endTime }: TableProps) {
  const [logs, setLogs] = useState<OktaLog[]>([])
  const [filter, setFilter] = useState({
    global: { value: '', matchMode: FilterMatchMode.CONTAINS }
  })
  const [globalFilter, setGlobalFilter] = useState<string>('')
  const [expandedRows, setExpandedRows] = useState<
    DataTableExpandedRows | undefined
  >(undefined)

  const { data, loading } = useGetSystemLogsQuery({
    variables: { email, startTime, endTime },
    skip: !email
  })

  useEffect(() => {
    if (data?.getSystemLogs) {
      setLogs(data.getSystemLogs as OktaLog[])
    }
  }, [data])

  const onFilterChange = (value: string) => {
    const updatedFilter = { ...filter }

    if (value.length >= 3 || value.length === 0) {
      updatedFilter.global.value = value
      setFilter(updatedFilter)
    }

    setGlobalFilter(value)
  }

  const onDateFilterChange = (range: Nullable<(Date | null)[]>) => {
    if (range && range[0] && range[1]) {
      const [rawStart, rawEnd] = range

      const start = new Date(rawStart)
      start.setHours(0, 0, 0, 0)

      const end = new Date(rawEnd)
      end.setHours(0, 0, 0, 0)
      end.setDate(end.getDate() + 1)

      const filteredLogs = (data?.getSystemLogs as OktaLog[]).filter((log) => {
        const logDate = new Date(log.time)

        return logDate >= start && logDate <= end
      })

      setLogs(filteredLogs)
    } else {
      setLogs((data?.getSystemLogs as OktaLog[]) ?? [])
    }
  }

  const onClearFilters = () => {
    setFilter({
      global: { value: '', matchMode: FilterMatchMode.CONTAINS }
    })
    setGlobalFilter('')
    setLogs((data?.getSystemLogs as OktaLog[]) ?? [])
  }

  const rowExpansionTemplate = (rowData: OktaLog) => {
    return <ExpandedRow targets={rowData.target} />
  }

  return (
    <DataTable
      className="mt-4"
      stripedRows
      paginator
      size="small"
      loading={loading}
      emptyMessage={loading ? 'Loading logs...' : 'No logs found.'}
      value={logs}
      rows={5}
      rowsPerPageOptions={[5, 10, 25, 50]}
      globalFilterFields={TABLE_COLUMNS.map(({ field }) => field)}
      filters={filter}
      globalFilter={globalFilter}
      expandedRows={expandedRows}
      onRowToggle={(e) => setExpandedRows(e.data as DataTableExpandedRows)}
      rowExpansionTemplate={rowExpansionTemplate}
      header={
        <TableHeader
          searchPlaceholder="Search by email, action, or event"
          datePlaceholder="Filter by dates"
          onSearchChange={onFilterChange}
          onDateRangeChange={onDateFilterChange}
          onClearFilters={onClearFilters}
        />
      }
    >
      <Column expander style={{ width: '3rem' }} />
      {TABLE_COLUMNS.map(({ field, header, sortable }, index) => {
        const id = `${field}-${index}`

        if (field === 'time') {
          return (
            <Column
              key={id}
              field={field}
              header={header}
              sortable={sortable || false}
              body={(rowData: OktaLog) =>
                dayjs(rowData.time).format('MMM D, YYYY, h:mm A')
              }
            />
          )
        }

        return (
          <TableColumn
            key={id}
            field={field}
            header={header}
            sortable={sortable || false}
          />
        )
      })}
    </DataTable>
  )
}
